import Modal from "react-modal";

import { X, Search } from "lucide-react";
import { useProductCombos } from "Hooks/useProductCombos";
import { CATEGORIES } from "Constants/Categories";
import { useState } from "react";
import Input from "Components/Form/Input";
import Select from "Components/Form/Select";
import { Button } from "Components/Button";
import { LoadingText } from "Components/LoadingText";
import { CartItem } from "Dtos/IBudget";
import { Product } from "Hooks/useProducts/interface";
import { useBudgetContext } from "Hooks/useBudgetContext";
import { Box } from "styles/globalComponents";
import { useCartAmount } from "Components/ServiceForm/ProductsResultSearch/useCartAmount";
import { createToast } from "Utils/toastFunc";
import { useSelector } from "react-redux";
import { Store } from "Reducers/interface";
import { useCart } from "Hooks/useCart";
import { useProductList } from "./SelectProducts/useProductList";
import { ModalProductsWarning } from "./ModalsProductsWarning";
import { useIATracking } from "./useIATracking";

interface ModalReplaceProductsProps {
  isOpen: boolean;
  onClose: () => void;
  itemsInCart: CartItem[];
}

export const ModalReplaceProducts = ({
  isOpen,
  onClose,
  itemsInCart,
}: ModalReplaceProductsProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: "",
    subcategory: "",
  });

  const {
    handleAddProduct,
    partnersOptions,
    closeModal,
    modal,
    preparation,
    cancelAddProduct,
    proceedAddProduct,
    handleConfirmPartnerSelection,
    handleChangePartner,
  } = useCartAmount();

  const { removeIAProductFromCart } = useCart();

  const { saveTrackingIAProducts, replaceItemWithTracking } = useIATracking();

  const { IAReponse } = useSelector((state: Store) => state.IABudget);

  const { productsList, loadingProducts, getProducts } = useProductList();

  const { combos, loadingCombos } = useProductCombos({
    includeCombo: ["categories", "subcategories", "status", "services"],
    searchBudget: true,
  });

  const { replaceProductSelected, loadingCart, partnerSelected, budget } =
    useBudgetContext();
  const [localSelectedProducts, setLocalSelectedProducts] = useState<Product[]>(
    []
  );

  const { matching_results } = IAReponse ?? {};

  const productBeingReplaced = matching_results?.resultados.find(
    (product) => product.id === replaceProductSelected?.product_id
  );

  const { categories, subcategories } = combos ?? {};

  const categoriesOpts = categories?.filter((category) =>
    [
      CATEGORIES.exam,
      CATEGORIES.checkup,
      CATEGORIES.test,
      CATEGORIES.consult,
    ].includes(category.id)
  );

  const subcategoriesOpts = subcategories?.filter(
    (subcategory) => subcategory.category_id === Number(filters.category)
  );

  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleSearch = () => {
    const searchParams = {
      search: searchTerm,
      categories: filters.category || "1,2,3",
      subcategory: filters.subcategory,
    };

    getProducts(searchParams);
  };

  const clearFilters = () => {
    setFilters({
      category: "",
      subcategory: "",
    });
    setSearchTerm("");
  };

  const isProductInCart = (productId: number) => {
    return itemsInCart.some((item) => item.product_id === productId);
  };

  const isProductReplacement = (productId: number) => {
    return replaceProductSelected?.product_id === productId;
  };

  const isProductSelected = (productId: number) => {
    return localSelectedProducts.some((p) => p.id === productId);
  };

  const handleProductSelect = (product: Product) => {
    const alreadyAddedProduct = localSelectedProducts.find(
      (prod) => prod.id === product.id
    );

    if (alreadyAddedProduct) return;
    setLocalSelectedProducts((prev) => [...prev, product]);
  };

  const handleAddProducts = async () => {
    try {
      setLoading(true);

      const promisesAddProducts = localSelectedProducts.map((product) =>
        handleAddProduct(product, replaceProductSelected?.budget_id)
      );

      await Promise.allSettled(promisesAddProducts);
      saveTrackingIAProducts({
        localSelectedProducts,
        productBeingReplaced,
      });

      if (productBeingReplaced && productBeingReplaced.id) {
        const cartItem = budget?.cart.find(
          (item) => item.product_id === productBeingReplaced.id
        );

        replaceItemWithTracking(
          Number(productBeingReplaced.id),
          localSelectedProducts
        );
        // removendo produto do carrinho
        if (cartItem) await removeIAProductFromCart("remove", 1, cartItem);
      }

      createToast(
        "success",
        `${localSelectedProducts.length} produto(s) substituído(s) com sucesso!`
      );
      onClose();
    } catch (error) {
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const removeSelectedProduct = (product: Product) => {
    const newSelection = localSelectedProducts.filter(
      (p) => p.id !== product.id
    );
    setLocalSelectedProducts(newSelection);
  };

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick
      onRequestClose={onClose}
      closeTimeoutMS={500}
      className="modal-content modal-content-cart"
      overlayClassName={{
        base: "modal modal-products-ia",
        afterOpen: "after-open",
        beforeClose: "before-close",
      }}
    >
      <div className="modal-header">
        <h2>Substituir Produto</h2>
        <button onClick={onClose} className="close-button">
          <X size={24} />
        </button>
      </div>

      <div className="modal-body">
        {replaceProductSelected && (
          <div className="original-product-info">
            <h4>Produto Original:</h4>
            <p>
              <strong>{replaceProductSelected.product_name}</strong>
            </p>
            <p>Código: {replaceProductSelected.product_code}</p>
          </div>
        )}

        <div className="search-section">
          <h4>Selecione o(s) produto(s) para realizar a substituição</h4>

          <div className="search-filters">
            <Box display="flex" align="end">
              {/* @ts-ignore */}

              <Input
                id="search-products"
                label=""
                type="text"
                placeholder="Buscar produtos..."
                value={searchTerm}
                onChange={(e: any) => setSearchTerm(e.target.value)}
                onKeyPress={(e: any) => e.key === "Enter" && handleSearch()}
              />

              <Select
                id="category-filter"
                label=""
                value={filters.category}
                onChange={(e: any) => {
                  handleFilterChange("subcategory", "");
                  handleFilterChange("category", e.target.value);
                }}
                options={
                  categoriesOpts?.map((cat) => ({
                    id: cat.id.toString(),
                    name: cat.category,
                  })) || []
                }
                placeholder="Categoria"
                optionName="name"
                readOnly={!categoriesOpts?.length}
              />

              {subcategoriesOpts && subcategoriesOpts?.length >= 1 && (
                <Select
                  id="subcategory-filter"
                  label="Subcategoria"
                  value={filters.subcategory}
                  onChange={(e: any) =>
                    handleFilterChange("subcategory", e.target.value)
                  }
                  options={
                    subcategoriesOpts?.map((cat) => ({
                      id: cat.id.toString(),
                      name: cat.subcategory,
                    })) || []
                  }
                  error=""
                  required={false}
                  purple={false}
                  placeholder="Subcategoria"
                  optionName="name"
                  small
                  readOnly={false}
                />
              )}

              <Button onClick={clearFilters} outlined>
                Limpar
              </Button>
              <Button onClick={handleSearch}>
                <Search size={16} />
                Buscar
              </Button>
            </Box>
          </div>
        </div>

        <div className="products-list">
          {loadingProducts && <LoadingText text="Carregando produtos..." />}

          {productsList && (
            <div className="products-table">
              <table>
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nome</th>
                    <th>Preço</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {productsList && productsList?.length >= 1 ? (
                    productsList?.map((product) => {
                      const hasPrice =
                        ("default_partner" in product &&
                          product.default_partner?.price) ||
                        (product.partners &&
                          product.partners.length > 0 &&
                          product.partners[0].price);

                      const productPrice =
                        "default_partner" in product &&
                        product.default_partner?.price
                          ? product.default_partner.price
                          : product.partners && product.partners.length > 0
                          ? product.partners[0].price
                          : 0;

                      const productStatus =
                        product.status?.status || "Sem status";
                      const isAvailable =
                        hasPrice &&
                        productStatus.toLowerCase() !== "indisponível";

                      return (
                        <tr
                          key={product.id}
                          className={`product-row 
                            ${isProductSelected(product.id) ? "selected" : ""} 
                            ${isProductInCart(product.id) ? "in-cart" : ""} ${
                            !isAvailable ? "unavailable" : ""
                          }`}
                          onClick={() => handleProductSelect(product)}
                        >
                          <td className="product-code text-saira">
                            {product.sku}
                          </td>
                          <td className="product-name">
                            {product.product}
                            {isProductInCart(product.id) && (
                              <span className="in-cart-badge">No carrinho</span>
                            )}
                            {isProductReplacement(product.id) && (
                              <span className="in-replacement-badge">
                                Em substituição
                              </span>
                            )}
                          </td>
                          <td className="product-price">
                            {hasPrice ? (
                              `R$ ${productPrice.toFixed(2).replace(".", ",")}`
                            ) : (
                              <span className="no-price-text">Sem preço</span>
                            )}
                          </td>
                          <td className="product-status">
                            <span className="status-badge">
                              {product.status?.status || "Sem status"}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center">
                        Nenhum resultado encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {localSelectedProducts.length > 0 && (
          <div className="selected-products">
            <h4>Produtos selecionados ({localSelectedProducts.length}):</h4>
            <div className="selected-list">
              {localSelectedProducts.map((product) => (
                <div key={product.id} className="selected-item">
                  <span>{product.product}</span>
                  <button
                    onClick={() => removeSelectedProduct(product)}
                    className="remove-selected"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="modal-footer">
        <Button onClick={onClose} outlined loading={loading} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleAddProducts}
          loading={loading}
          disabled={loading}
        >
          Confirmar{" "}
        </Button>
      </div>

      <ModalProductsWarning
        closeModal={closeModal}
        modal={modal}
        handleChangePartner={handleChangePartner}
        cancelAddProduct={cancelAddProduct}
        proceedAddProduct={proceedAddProduct}
        preparation={preparation}
        partnerSelected={partnerSelected}
        partnersOptions={partnersOptions}
        handleConfirmPartnerSelection={handleConfirmPartnerSelection}
        loadingCart={loadingCart}
      />
    </Modal>
  );
};
